/**
 * Photos API — Person photo upload, download, and deletion
 *
 * Endpoints:
 *   POST   /api/Persons/{id}/photo           — Upload (multipart/form-data)
 *   GET    /api/Persons/{id}/photo           — Original photo
 *   GET    /api/Persons/{id}/photo/thumbnail — Thumbnail (~128px)
 *   DELETE /api/Persons/{id}/photo           — Delete photo
 */

import { apiClient } from '@/api/client'
import type { AxiosProgressEvent } from 'axios'

export type UploadProgressCallback = (progress: number) => void

export const photosApi = {
  /**
   * Upload a photo for a person.
   * Accepts File (from file input) or Blob (from canvas/data URL conversion).
   */
  async upload(
    personId: number,
    file: File | Blob,
    onProgress?: UploadProgressCallback
  ): Promise<void> {
    const formData = new FormData()
    formData.append('file', file, file instanceof File ? file.name : 'photo.jpg')
    // No explicit Content-Type! Axios detects FormData and auto-sets
    // multipart/form-data with the correct boundary. Setting it manually
    // strips the boundary → 400/415 on the backend.
    if (onProgress) {
      await apiClient.post(`/Persons/${personId}/photo`, formData, {
        onUploadProgress: (e: AxiosProgressEvent) => {
          const pct = e.total ? Math.round((e.loaded * 100) / e.total) : 0
          onProgress(pct)
        },
      })
    } else {
      await apiClient.post(`/Persons/${personId}/photo`, formData)
    }
  },

  /**
   * Get the thumbnail as a Blob.
   * Returns the Blob and the ETag header (if present).
   */
  async getThumbnail(
    personId: number,
    etag?: string
  ): Promise<{ blob: Blob; etag: string | undefined }> {
    const headers: Record<string, string> = {}
    if (etag) headers['If-None-Match'] = etag
    const response = await apiClient.get(`/Persons/${personId}/photo/thumbnail`, {
      responseType: 'blob',
      headers,
      // 304 Not Modified should not throw
      validateStatus: (status) => status === 200 || status === 304,
    })
    return {
      blob: response.status === 304 ? new Blob() : response.data,
      etag: response.headers['etag'] as string | undefined,
    }
  },

  /**
   * Get the original photo as a Blob (for full-resolution preview).
   */
  async getOriginal(personId: number): Promise<Blob> {
    const response = await apiClient.get(`/Persons/${personId}/photo`, {
      responseType: 'blob',
    })
    return response.data
  },

  /** Delete a person's photo. */
  async delete(personId: number): Promise<void> {
    await apiClient.delete(`/Persons/${personId}/photo`)
  },
}
